import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../store";

import { Permissions } from "@/interfaces/globals";
import { getCustomerId, getWorkspace } from "@/utils/helpers";

export type TUserPermission = {
  action: string;
  resource: Permissions;
  workspace: string | "*";
};

export type TCustomerPermission = Omit<TUserPermission, "workspace"> & {
  customer: string;
};

const initialState: {
  data: TUserPermission[];
  customerData: TCustomerPermission[];
} = {
  data: [],
  customerData: [],
};

export const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    set: (state, action: PayloadAction<TUserPermission[]>) => {
      state.data = action.payload;
    },
    setCustomer: (state, action: PayloadAction<TCustomerPermission[]>) => {
      state.customerData = action.payload;
    },
  },
});

export const { set, setCustomer } = permissionSlice.actions;

export default permissionSlice.reducer;

const uPermissions = (state: RootState): TUserPermission[] =>
  state.permissions.data;

const customerPermissions = (state: RootState): TCustomerPermission[] =>
  state.permissions.customerData;

export const hasReadPermission = (
  ...permissions: Permissions[]
): ((x: RootState) => boolean) =>
  createSelector([uPermissions], (uPermission) => {
    const currentWorkspace = getWorkspace();

    if (permissions.length == 0) {
      return true;
    }

    if (!uPermission) {
      return false;
    }
    if (uPermission.length === 0) {
      return false;
    }

    if (
      uPermission.find(
        (p) =>
          p.resource === Permissions.ALL &&
          (p.workspace === "*" || p.workspace === currentWorkspace),
      )
    ) {
      return true;
    }

    return permissions.some((permission) =>
      uPermission.find(
        (p) =>
          p.resource === permission &&
          (p.workspace === "*" || p.workspace === currentWorkspace) &&
          p.action === "READ",
      ),
    );
  });

export const hasWritePermission = (
  ...permissions: Permissions[]
): ((x: RootState) => boolean) =>
  createSelector([uPermissions], (uPermission) => {
    const currentWorkspace = getWorkspace();
    if (!uPermission) {
      return false;
    }
    if (uPermission.length === 0) {
      return false;
    }

    if (
      uPermission.find(
        (p) =>
          p.resource === Permissions.ALL &&
          (p.workspace === "*" || p.workspace === currentWorkspace) &&
          p.action === "WRITE",
      )
    ) {
      return true;
    }

    return permissions.some((permission) =>
      uPermission.find(
        (p) =>
          p.resource === permission &&
          (p.workspace === "*" || p.workspace === currentWorkspace) &&
          p.action === "WRITE",
      ),
    );
  });

//for customer user permissions
export const hasCustomerReadPermission = (
  ...permissions: Permissions[]
): ((x: RootState) => boolean) =>
  createSelector([customerPermissions], (customerPermissions) => {
    const currentCustomer = getCustomerId();

    if (permissions.length == 0) {
      return true;
    }

    if (!customerPermissions) {
      return false;
    }
    if (customerPermissions.length === 0) {
      return false;
    }

    if (
      customerPermissions.find(
        (p) =>
          p.resource === Permissions.ALL &&
          (p.customer === "*" || p.customer === currentCustomer),
      )
    ) {
      return true;
    }

    return permissions.some((permission) =>
      customerPermissions.find(
        (p) =>
          p.resource === permission &&
          (p.customer === "*" || p.customer === currentCustomer) &&
          p.action === "READ",
      ),
    );
  });

export const hasCustomerWritePermission = (
  ...permissions: Permissions[]
): ((x: RootState) => boolean) =>
  createSelector([customerPermissions], (customerPermissions) => {
    const currentCustomer = getCustomerId();

    if (!customerPermissions) {
      return false;
    }
    if (customerPermissions.length === 0) {
      return false;
    }

    if (
      customerPermissions.find(
        (p) =>
          p.resource === Permissions.ALL &&
          (p.customer === "*" || p.customer === currentCustomer),
      )
    ) {
      return true;
    }

    return permissions.some((permission) =>
      customerPermissions.find(
        (p) =>
          p.resource === permission &&
          (p.customer === "*" || p.customer === currentCustomer) &&
          p.action === "WRITE",
      ),
    );
  });
